/// <reference path="../svg.d.ts" />
import React from 'react';
import { RectangleHorizontal } from 'lucide-react';
import { getIconProps, IconProps, ICON } from '../utils';
import TruckSvg from '../assets/vehicles/truck.svg?react';
import { VehicleTypes } from '@hero/schema';
import { cn } from '@hero/web-lib';

const SIZE_MAP: Record<'small' | 'medium' | 'large', number> = {
  small: 16,
  medium: 24,
  large: 32,
} as const;

// Helper function to calculate final width/height for vehicle icons
const getVehicleIconSize = (
  props: IconProps,
  iconProps: React.SVGProps<SVGSVGElement>,
): { width: number | undefined; height: number | undefined; className: string | undefined } => {
  const { width, height, className } = iconProps;
  // Always set width/height based on size prop, unless className is provided (which handles sizing)
  const iconSize = className ? undefined : SIZE_MAP[props.size || ICON.size];
  const finalWidth = className
    ? typeof width === 'number'
      ? width
      : undefined
    : typeof width === 'number'
    ? width
    : iconSize;
  const finalHeight = className
    ? typeof height === 'number'
      ? height
      : undefined
    : typeof height === 'number'
    ? height
    : iconSize;
  return { width: finalWidth, height: finalHeight, className };
};

export const VehicleMainIcon: React.FC<IconProps> = props => {
  // Use the provided strokeWidth, or default to 2 minimum
  const minStrokeWidth = props.strokeWidth ? Math.max(Number(props.strokeWidth), 2) : 2;
  const iconProps = getIconProps({ ...props, strokeWidth: minStrokeWidth });
  const { style, ...restIconProps } = iconProps;
  const {
    width: finalWidth,
    height: finalHeight,
    className,
  } = getVehicleIconSize(props, iconProps);

  return (
    <svg
      viewBox="0 0 40 40"
      fill="none"
      width={finalWidth}
      height={finalHeight}
      className={className}
      style={{ ...style, width: finalWidth, height: finalHeight }}
      {...restIconProps}>
      <path
        d="M25.0001 13.0205H28.8955C29.3032 13.0205 29.507 13.0205 29.6988 13.0637C29.8689 13.102 30.0314 13.1651 30.1806 13.2508C30.3487 13.3474 30.4929 13.4825 30.7811 13.7527L37.5524 20.1008C37.8406 20.371 37.9847 20.5061 38.0878 20.6638C38.1792 20.8036 38.2465 20.956 38.2874 21.1155C38.3334 21.2953 38.3334 21.4864 38.3334 21.8685V26.3018C38.3334 27.0298 38.3334 27.3938 38.2065 27.681C38.0374 28.0638 37.7129 28.368 37.3045 28.5266C36.9983 28.6455 36.61 28.6455 35.8334 28.6455M27.5001 28.6455H25.0001M25.0001 28.6455V13.333C25.0001 11.5828 25.0001 10.7078 24.6368 10.0393C24.3172 9.45128 23.8073 8.97322 23.18 8.67361C22.467 8.33301 21.5336 8.33301 19.6667 8.33301H7.00008C5.13324 8.33301 4.19982 8.33301 3.48678 8.67361C2.85957 8.97322 2.34964 9.45128 2.03006 10.0393C1.66675 10.7078 1.66675 11.5828 1.66675 13.333V25.5205C1.66675 27.2464 3.15913 28.6455 5.00008 28.6455M25.0001 28.6455H15.0001M15.0001 28.6455C15.0001 31.2343 12.7615 33.333 10.0001 33.333C7.23866 33.333 5.00008 31.2343 5.00008 28.6455M15.0001 28.6455C15.0001 26.0567 12.7615 23.958 10.0001 23.958C7.23866 23.958 5.00008 26.0567 5.00008 28.6455M35.8334 29.4268C35.8334 31.5841 33.9679 33.333 31.6667 33.333C29.3656 33.333 27.5001 31.5841 27.5001 29.4268C27.5001 27.2694 29.3656 25.5205 31.6667 25.5205C33.9679 25.5205 35.8334 27.2694 35.8334 29.4268Z"
        stroke="currentColor"
        strokeWidth={iconProps.strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};
// Semi Truck Icon
const SemiTruckIcon: React.FC<IconProps> = props => {
  const iconProps = getIconProps(props);
  const { style, ...restIconProps } = iconProps;
  const {
    width: finalWidth,
    height: finalHeight,
    className,
  } = getVehicleIconSize(props, iconProps);

  return (
    <svg
      viewBox="0 0 40 40"
      fill="none"
      width={finalWidth}
      height={finalHeight}
      className={className}
      style={{ ...style, width: finalWidth, height: finalHeight }}
      {...restIconProps}>
      <path
        d="M7.49992 29.1664C7.49992 31.0074 6.00753 32.4998 4.16659 32.4998C2.32564 32.4998 0.833252 31.0074 0.833252 29.1664C0.833252 27.3255 2.32564 25.8331 4.16659 25.8331M7.49992 29.1664C7.49992 27.3255 6.00753 25.8331 4.16659 25.8331M7.49992 29.1664H9.99992M4.16659 25.8331H19.9999M27.4999 29.1664C27.4999 31.0074 28.9923 32.4998 30.8333 32.4998C32.0671 32.4998 33.1443 31.8294 33.7206 30.8331M27.4999 29.1664C27.4999 27.3255 28.9923 25.8331 30.8333 25.8331C31.121 25.8331 31.4002 25.8695 31.6666 25.9381C33.1043 26.3081 34.1666 27.6132 34.1666 29.1664C34.1666 29.7736 34.0043 30.3428 33.7206 30.8331M27.4999 29.1664H22.4999M22.4999 29.1664C22.4999 29.9998 21.6666 30.8331 20.8333 30.8331H11.6666C10.8333 30.8331 9.99992 29.9998 9.99992 29.1664M22.4999 29.1664V27.4998C22.4999 26.6664 21.6666 25.8331 20.8333 25.8331H19.9999M9.99992 29.1664V27.4998C9.99992 26.6664 10.8333 25.8331 11.6666 25.8331H19.9999M19.9999 25.8331C19.9999 20.5553 19.9999 9.83309 19.9999 9.16642C19.9999 8.33309 19.9999 7.08309 22.4999 7.49975C24.9999 7.91642 31.2499 8.75 33.7499 9.16667C36.2499 9.58333 37.4999 10 37.4999 11.25C37.4999 11.6874 37.5531 12.8317 37.6245 14.1667M37.9166 19.1667C38.3333 19.1667 39.1666 19.5 39.1666 20.8333C39.1666 21.6178 39.1666 24.9919 39.1666 26.6667C39.1666 28.5501 39.1666 28.7256 39.1666 29.1667C39.1666 30 38.7504 30.8331 37.5002 30.8331C36.5 30.8331 35.1193 30.8331 33.7206 30.8331M37.9166 19.1667C37.8384 17.9165 37.7164 15.8838 37.6245 14.1667M37.9166 19.1667H32.4999C31.2499 19.1667 29.9999 18.75 29.9999 17.5C29.9999 16.6959 29.9999 16.0921 29.9999 15.8333C29.9999 14.5833 30.8333 14.1667 31.6666 14.1667C32.3333 14.1667 35.9163 14.1667 37.6245 14.1667M22.4999 15C22.4999 14.5833 22.9166 14.1667 23.3333 14.1667C23.7499 14.1667 25.4166 14.1667 25.8333 14.1667C26.2499 14.1667 26.6666 14.5833 26.6666 15C26.6666 15.4167 26.6666 17.0833 26.6666 17.5C26.6666 17.9167 26.2499 18.3333 25.8333 18.3333C25.4166 18.3333 23.7499 18.3333 23.3333 18.3333C22.9166 18.3333 22.4999 17.9167 22.4999 17.5C22.4999 17.1667 22.4999 15.4167 22.4999 15Z"
        stroke="currentColor"
        strokeWidth={iconProps.strokeWidth}
        strokeLinecap="round"
      />
    </svg>
  );
};

// Sprinter Icon
const SprinterIcon: React.FC<IconProps> = props => {
  const iconProps = getIconProps(props);
  const { style, ...restIconProps } = iconProps;
  const {
    width: finalWidth,
    height: finalHeight,
    className,
  } = getVehicleIconSize(props, iconProps);

  return (
    <svg
      viewBox="0 0 40 40"
      fill="none"
      width={finalWidth}
      height={finalHeight}
      className={className}
      style={{ ...style, width: finalWidth, height: finalHeight }}
      {...restIconProps}>
      <path
        d="M34.5833 20.0003H29.1666C28.3333 20.0003 27.4999 19.167 27.4999 18.3337V15.8337C27.4999 15.0003 28.3333 14.167 29.1666 14.167H31.8504M34.5833 20.0003C33.9433 18.5605 32.852 16.181 31.8504 14.167M34.5833 20.0003C35.5555 20.417 37.6666 21.3337 38.3333 21.667C39.1666 22.0837 39.1666 22.917 39.1666 23.3337V26.667M31.8504 14.167C31.1129 12.6839 30.424 11.3991 29.9999 10.8337C28.7499 9.16699 27.4999 9.16699 25.8333 9.16699H4.16659C2.91659 9.16699 1.66659 10.417 1.66659 11.667V26.667M1.66659 26.667H3.33325C3.74992 26.667 4.16659 27.0837 4.16659 27.5003V29.167M1.66659 26.667C1.24992 26.667 0.833252 27.0837 0.833252 27.5003V28.3337C0.833252 28.7503 1.24992 29.167 1.66659 29.167H4.16659M4.16659 29.167H6.69612M36.637 29.167C36.6565 29.0309 36.6666 28.8918 36.6666 28.7503C36.6666 27.9342 36.3314 27.1964 35.7912 26.667M36.637 29.167C36.4349 30.5804 35.2193 31.667 33.7499 31.667C32.2806 31.667 31.065 30.5804 30.8628 29.167M36.637 29.167H37.4999C38.3333 29.167 39.1666 28.7503 39.1666 27.5003V26.667M6.69612 29.167C6.8983 30.5804 8.11389 31.667 9.58325 31.667C11.0526 31.667 12.2682 30.5804 12.4704 29.167M6.69612 29.167C6.67666 29.0309 6.66659 28.8918 6.66659 28.7503C6.66659 27.1395 7.97242 25.8337 9.58325 25.8337C11.1941 25.8337 12.4999 27.1395 12.4999 28.7503C12.4999 28.8918 12.4898 29.0309 12.4704 29.167M12.4704 29.167H30.8628M30.8628 29.167C30.8433 29.0309 30.8333 28.8918 30.8333 28.7503C30.8333 27.1395 32.1391 25.8337 33.7499 25.8337C34.5446 25.8337 35.2651 26.1515 35.7912 26.667M39.1666 26.667H35.7912M14.5833 26.667H28.7499"
        stroke="currentColor"
        strokeWidth={iconProps.strokeWidth}
        strokeLinecap="round"
      />
    </svg>
  );
};

// Flatbed Icon (simple version)
const FlatbedIcon: React.FC<IconProps> = props => {
  const iconProps = getIconProps(props);
  const { style, ...restIconProps } = iconProps;
  const {
    width: finalWidth,
    height: finalHeight,
    className,
  } = getVehicleIconSize(props, iconProps);

  return (
    <svg
      viewBox="0 0 40 40"
      fill="none"
      width={finalWidth}
      height={finalHeight}
      className={className}
      style={{ ...style, width: finalWidth, height: finalHeight }}
      {...restIconProps}>
      <path
        d="M1.66675 23.333V22.583C1.25253 22.583 0.916748 22.9188 0.916748 23.333H1.66675ZM29.0834 8.33301C29.0834 7.91879 28.7476 7.58301 28.3334 7.58301C27.9192 7.58301 27.5834 7.91879 27.5834 8.33301H29.0834ZM35.8334 13.333L35.2094 13.749L35.2506 13.8108L35.3031 13.8633L35.8334 13.333ZM35.0001 25.9163C34.5859 25.9163 34.2501 26.2521 34.2501 26.6663C34.2501 27.0806 34.5859 27.4163 35.0001 27.4163V25.9163ZM0.916748 23.333V24.9997H2.41675V23.333H0.916748ZM0.916748 24.9997C0.916748 25.9108 1.31318 26.5526 1.85352 26.9417C2.35562 27.3032 2.93562 27.4163 3.33341 27.4163V25.9163C3.17565 25.9163 2.92232 25.8628 2.72998 25.7244C2.57588 25.6134 2.41675 25.4219 2.41675 24.9997H0.916748ZM11.6667 27.4163H26.6667V25.9163H11.6667V27.4163ZM29.0834 23.333V10.833H27.5834V23.333H29.0834ZM29.0834 10.833V8.33301H27.5834V10.833H29.0834ZM28.3334 11.583H31.6667V10.083H28.3334V11.583ZM31.6667 11.583C32.4572 11.583 33.2086 11.9141 33.8531 12.3788C34.4976 12.8437 34.9779 13.4019 35.2094 13.749L36.4575 12.917C36.1333 12.4308 35.5303 11.739 34.7304 11.1622C33.9305 10.5853 32.8763 10.083 31.6667 10.083V11.583ZM35.3031 13.8633C35.6776 14.2379 36.2618 14.9369 36.7475 15.7725C37.2395 16.6187 37.5834 17.5244 37.5834 18.333H39.0834C39.0834 17.1417 38.594 15.964 38.0443 15.0185C37.4884 14.0624 36.8226 13.2615 36.3637 12.8027L35.3031 13.8633ZM37.5834 18.333V24.1663H39.0834V18.333H37.5834ZM37.5834 24.1663C37.5834 24.8799 37.4086 25.2374 37.1357 25.4557C36.8184 25.7095 36.2 25.9163 35.0001 25.9163V27.4163C36.3001 27.4163 37.3484 27.2065 38.0728 26.627C38.8416 26.012 39.0834 25.1194 39.0834 24.1663H37.5834ZM34.2501 27.4997C34.2501 29.3866 32.7204 30.9163 30.8334 30.9163V32.4163C33.5488 32.4163 35.7501 30.2151 35.7501 27.4997H34.2501ZM30.8334 30.9163C28.9464 30.9163 27.4167 29.3866 27.4167 27.4997H25.9167C25.9167 30.2151 28.118 32.4163 30.8334 32.4163V30.9163ZM27.4167 27.4997C27.4167 25.6127 28.9464 24.083 30.8334 24.083V22.583C28.118 22.583 25.9167 24.7843 25.9167 27.4997H27.4167ZM30.8334 24.083C32.7204 24.083 34.2501 25.6127 34.2501 27.4997H35.7501C35.7501 24.7843 33.5488 22.583 30.8334 22.583V24.083ZM10.9167 27.4997C10.9167 29.3866 9.38705 30.9163 7.50008 30.9163V32.4163C10.2155 32.4163 12.4167 30.2151 12.4167 27.4997H10.9167ZM7.50008 30.9163C5.61311 30.9163 4.08341 29.3866 4.08341 27.4997H2.58341C2.58341 30.2151 4.78468 32.4163 7.50008 32.4163V30.9163ZM4.08341 27.4997C4.08341 25.6127 5.61311 24.083 7.50008 24.083V22.583C4.78468 22.583 2.58341 24.7843 2.58341 27.4997H4.08341ZM7.50008 24.083C9.38705 24.083 10.9167 25.6127 10.9167 27.4997H12.4167C12.4167 24.7843 10.2155 22.583 7.50008 22.583V24.083Z"
        fill="currentColor"
      />
    </svg>
  );
};

// Dry Van Icon
const CurtainSideIcon: React.FC<IconProps> = props => {
  const iconProps = getIconProps(props);
  const { style, ...restIconProps } = iconProps;
  const {
    width: finalWidth,
    height: finalHeight,
    className,
  } = getVehicleIconSize(props, iconProps);

  return (
    <svg
      viewBox="0 0 40 40"
      fill="none"
      width={finalWidth}
      height={finalHeight}
      className={className}
      style={{ ...style, width: finalWidth, height: finalHeight }}
      {...restIconProps}>
      <path
        d="M37.5 25.8333C37.5 27.214 36.3807 28.3333 35 28.3333C33.9115 28.3333 32.9854 27.6376 32.6423 26.6666M37.5 25.8333C37.5 24.4526 36.3807 23.3333 35 23.3333C33.6193 23.3333 32.5 24.4526 32.5 25.8333C32.5 26.1255 32.5501 26.406 32.6423 26.6666M37.5 25.8333L37.9167 25.8333C38.3333 25.8333 38.75 25.4167 38.75 25C38.75 24.6667 38.75 22.0833 38.75 20.8333M3.47559 26.6666C3.81879 27.6376 4.74482 28.3333 5.83333 28.3333C6.92185 28.3333 7.84788 27.6376 8.19108 26.6666M3.47559 26.6666C3.38346 26.406 3.33333 26.1255 3.33333 25.8333C3.33333 25.193 3.57403 24.609 3.96987 24.1667M3.47559 26.6666C3.15039 26.6666 2.41667 26.6666 2.08333 26.6666C1.66667 26.6666 1.25 26.25 1.25 25.8333C1.25 25.7339 1.25 25.0922 1.25 24.1667M27.9476 24.1667C27.9476 21.25 27.9476 15.3395 27.9476 15.0309C27.9476 14.6452 27.5 14.1667 27.0833 14.1667C26.6667 14.1667 2.5 14.1667 2.11426 14.1667C1.72852 14.1667 1.25 14.5833 1.25 15.0309C1.25 15.3717 1.25 21.2139 1.25 24.1667M27.9476 24.1667H16.0301M27.9476 24.1667H30.8333M16.0301 24.1667C15.5724 23.6552 14.9071 23.3333 14.1667 23.3333C13.4262 23.3333 12.761 23.6552 12.3032 24.1667M16.0301 24.1667C16.426 24.609 16.6667 25.193 16.6667 25.8333C16.6667 26.1255 16.6165 26.406 16.5244 26.6666M12.3032 24.1667C11.9074 24.609 11.6667 25.193 11.6667 25.8333C11.6667 26.1255 11.7168 26.406 11.8089 26.6666M12.3032 24.1667H7.6968M7.6968 24.1667C7.23903 23.6552 6.57377 23.3333 5.83333 23.3333C5.0929 23.3333 4.42764 23.6552 3.96987 24.1667M7.6968 24.1667C8.09264 24.609 8.33333 25.193 8.33333 25.8333C8.33333 26.1255 8.2832 26.406 8.19108 26.6666M3.96987 24.1667H1.25M8.19108 26.6666H11.8089M11.8089 26.6666C12.1521 27.6376 13.0782 28.3333 14.1667 28.3333C15.2552 28.3333 16.1812 27.6376 16.5244 26.6666M16.5244 26.6666H30.8333M32.6423 26.6666H30.8333M30.8333 26.6666V24.1667M28.3333 11.25L30.8333 12.0833V14.1667M30.8333 14.1667H36.6667L38.75 20.8333M30.8333 14.1667V20.8333M38.75 20.8333H30.8333M30.8333 20.8333V24.1667"
        stroke="currentColor"
        strokeWidth={iconProps.strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const VehicleDynamicIcon: React.FC<IconProps & { vehicle: VehicleTypes }> = props => {
  const { vehicle, ...rest } = props;
  //const className = cn(rest.className, 'w-6 h-6');
  switch (vehicle) {
    case 'truck_flatbed':
      return <SemiTruckIcon {...rest} />;
    case 'truck_curtain_side':
      return <SemiTruckIcon {...rest} />;
    case 'truck_chilled':
      return <SemiTruckIcon {...rest} />;
    case 'truck_closed':
      return <SemiTruckIcon {...rest} />;
    case 'truck_flatbed_container':
      return <SemiTruckIcon {...rest} />;
    default:
      return <VehicleMainIcon {...rest} />;
  }
};

export const PlateIcon: React.FC<IconProps> = props => {
  const iconProps = getIconProps(props);
  return <RectangleHorizontal {...iconProps} />;
};
